import { useState, useCallback, useRef } from 'react';

const PROPERTY_API_URL = 'https://property-lookup-api.anassk.workers.dev';
const PROPERTY_API_KEY = '19285196e984c67c9c96784becbd3464e692815a4393e4c948be460a2019182e';

// Local cache key for enriched leads
const CACHE_KEY = 'leadgen_enriched_leads';

// Get user ID for daily limit tracking
function getUserId() {
  return localStorage.getItem('leadgen_client_id') || `user_${Date.now()}`;
}

// Get cached enriched leads from localStorage
function getCachedLeads() {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    return cached ? JSON.parse(cached) : {};
  } catch {
    return {};
  }
}

// Validate cached data looks reasonable (detect corrupted cache entries)
function isValidCachedData(data) {
  if (!data || !data.success) return false;

  // Reject if no property data at all (incomplete/old cache entry)
  if (!data.property || !data.property.squareFootage) {
    console.warn('Rejecting cached data - missing property data');
    return false;
  }

  // Reject obviously invalid data (sqft > 30,000 is unreasonable for residential)
  const sqft = data.property.squareFootage;
  if (sqft > 30000) {
    console.warn('Rejecting cached data with invalid sqft:', sqft);
    return false;
  }

  // Reject if roof estimate is absurdly high (> $500k for a residential roof)
  const roofCost = data.roofEstimate?.costEstimates?.asphalt?.estimate;
  if (roofCost && roofCost > 500000) {
    console.warn('Rejecting cached data with invalid roof cost:', roofCost);
    return false;
  }
  return true;
}

// Clear invalid entries from cache on startup
function cleanupInvalidCache() {
  try {
    const cached = getCachedLeads();
    let cleaned = 0;
    Object.keys(cached).forEach(key => {
      if (!isValidCachedData(cached[key])) {
        delete cached[key];
        cleaned++;
      }
    });
    if (cleaned > 0) {
      localStorage.setItem(CACHE_KEY, JSON.stringify(cached));
      console.log(`Cleaned ${cleaned} invalid cache entries`);
    }
  } catch (e) {
    console.warn('Failed to cleanup cache:', e);
  }
}

// Run cleanup on module load
cleanupInvalidCache();

// Save enriched lead to local cache
function cacheEnrichedLead(addressKey, data) {
  try {
    const cached = getCachedLeads();
    cached[addressKey] = {
      ...data,
      cachedAt: Date.now()
    };
    // Keep only last 500 leads to prevent localStorage bloat
    const keys = Object.keys(cached);
    if (keys.length > 500) {
      const oldest = keys.sort((a, b) => cached[a].cachedAt - cached[b].cachedAt).slice(0, 100);
      oldest.forEach(k => delete cached[k]);
    }
    localStorage.setItem(CACHE_KEY, JSON.stringify(cached));
  } catch (e) {
    console.warn('Failed to cache lead:', e);
  }
}

// Normalize address for cache key
function normalizeAddress(address) {
  return address
    .toUpperCase()
    .replace(/[^\w\s]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

export function usePropertyLookup(userEmail = null) {
  const [isLookingUp, setIsLookingUp] = useState(false);
  const [lookupProgress, setLookupProgress] = useState({ current: 0, total: 0 });
  const [lookupError, setLookupError] = useState(null);
  const [apiUsage, setApiUsage] = useState(null);
  const [dailyLimit, setDailyLimit] = useState(null);
  const abortRef = useRef(null);

  const userId = getUserId();
  // Store email for admin unlimited access
  const emailRef = useRef(userEmail);
  emailRef.current = userEmail;

  // Lookup a single property
  const lookupProperty = useCallback(async (address) => {
    const key = normalizeAddress(address);

    // Check local cache first
    const cached = getCachedLeads();
    if (cached[key] && Date.now() - cached[key].cachedAt < 7 * 24 * 60 * 60 * 1000 && isValidCachedData(cached[key])) { // 7 day cache + validation
      return { ...cached[key], fromLocalCache: true };
    }

    // Call API with userId and email for daily limit tracking
    const response = await fetch(`${PROPERTY_API_URL}/lookup`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-API-Key': PROPERTY_API_KEY,
        'X-User-ID': userId
      },
      body: JSON.stringify({ address, userId, email: emailRef.current })
    });

    const data = await response.json();

    // Update daily limit from response
    if (data.dailyLimit) {
      setDailyLimit(data.dailyLimit);
    }

    if (data.success) {
      cacheEnrichedLead(key, data);
    }

    return data;
  }, [userId]);

  // Batch lookup multiple properties with rate limiting
  const lookupBatch = useCallback(async (addresses, onProgress) => {
    if (addresses.length === 0) return [];

    setIsLookingUp(true);
    setLookupError(null);
    setLookupProgress({ current: 0, total: addresses.length });

    const results = [];
    const cached = getCachedLeads();
    const toFetch = [];
    const fetchIndices = [];

    // Separate cached vs need-to-fetch
    addresses.forEach((addr, i) => {
      const key = normalizeAddress(addr);
      if (cached[key] && Date.now() - cached[key].cachedAt < 7 * 24 * 60 * 60 * 1000 && isValidCachedData(cached[key])) {
        results[i] = { ...cached[key], fromLocalCache: true };
      } else {
        toFetch.push(addr);
        fetchIndices.push(i);
      }
    });

    const localCacheHits = addresses.length - toFetch.length;
    if (localCacheHits > 0) {
      console.log(`📦 Local cache hits: ${localCacheHits}/${addresses.length}`);
    }

    if (toFetch.length === 0) {
      setIsLookingUp(false);
      setLookupProgress({ current: addresses.length, total: addresses.length });
      return results;
    }

    // Batch API call (max 50 per batch)
    const BATCH_SIZE = 50;

    for (let i = 0; i < toFetch.length; i += BATCH_SIZE) {
      const batch = toFetch.slice(i, i + BATCH_SIZE);

      try {
        const response = await fetch(`${PROPERTY_API_URL}/batch`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-API-Key': PROPERTY_API_KEY,
            'X-User-ID': userId
          },
          body: JSON.stringify({ addresses: batch, userId, email: emailRef.current })
        });

        const data = await response.json();

        // Check if blocked by daily limit
        if (data.dailyLimit) {
          setDailyLimit(data.dailyLimit);
          if (data.dailyLimit.remaining === 0) {
            setLookupError(`Daily limit reached (${data.dailyLimit.limit}/day). Try again tomorrow.`);
            setIsLookingUp(false);
            return results;
          }
        }

        // Check if blocked by API usage
        if (data.blocked) {
          setLookupError('API limit reached. Contact admin to add more credits.');
          setApiUsage(data.usage);
          setIsLookingUp(false);
          return results; // Return what we have so far
        }

        if (data.results) {
          data.results.forEach((result, j) => {
            const globalIndex = fetchIndices[i + j];
            results[globalIndex] = result;

            if (result.success) {
              const key = normalizeAddress(batch[j]);
              cacheEnrichedLead(key, result);
            }
          });
        }

        // Update usage from response
        if (data.usage) {
          setApiUsage(data.usage);
        }

        setLookupProgress({ current: localCacheHits + i + batch.length, total: addresses.length });
        if (onProgress) onProgress(localCacheHits + i + batch.length, addresses.length);

        // Small delay between batches to avoid rate limiting
        if (i + BATCH_SIZE < toFetch.length) {
          await new Promise(r => setTimeout(r, 200));
        }
      } catch (e) {
        console.error('Batch lookup failed:', e);
        setLookupError(e.message);
        // Fill failed results
        batch.forEach((_, j) => {
          const globalIndex = fetchIndices[i + j];
          if (!results[globalIndex]) {
            results[globalIndex] = { success: false, error: e.message };
          }
        });
      }
    }

    setIsLookingUp(false);
    return results;
  }, [userId]);

  // Enrich leads with property data
  const enrichLeads = useCallback(async (leads, onProgress) => {
    if (leads.length === 0) return leads;

    // Build full addresses
    const addresses = leads.map(lead => {
      const parts = [lead.streetAddress, lead.city, lead.state, lead.zip].filter(Boolean);
      return parts.join(', ');
    });

    const results = await lookupBatch(addresses, onProgress);

    // Merge results into leads
    return leads.map((lead, i) => {
      const result = results[i];
      if (!result?.success) {
        return { ...lead, lookupFailed: true };
      }

      return {
        ...lead,
        // Core property data
        enriched: true,
        fromLocalCache: result.fromLocalCache || false,
        cached: result.cached || false,

        // Owner data (most important for flyers!)
        ownerName: result.owner?.names?.filter(n => n && n.trim()).join(', ') || lead.ownerName,
        ownerType: result.owner?.type,
        ownerMailingAddress: result.owner?.mailingAddress,
        ownerOccupied: result.ownerOccupied ?? lead.ownerOccupied,

        // Property details
        propertyType: result.property?.type,
        bedrooms: result.property?.bedrooms || lead.bedrooms,
        bathrooms: result.property?.bathrooms || lead.bathrooms,
        sqft: result.property?.squareFootage || lead.sqft,
        lotSize: result.property?.lotSize,
        yearBuilt: result.property?.yearBuilt || lead.yearBuilt,
        subdivision: result.property?.subdivision,

        // Features
        floorCount: result.features?.floorCount,
        roofType: result.features?.roofType,
        hasGarage: result.features?.garage,
        garageSpaces: result.features?.garageSpaces,
        hasPool: result.features?.pool,
        coolingType: result.features?.cooling,
        heatingType: result.features?.heating,

        // Valuation
        assessedValue: result.valuation?.assessedValue || lead.assessedValue,
        lastSaleDate: result.valuation?.lastSaleDate,
        lastSalePrice: result.valuation?.lastSalePrice,
        propertyTax: result.valuation?.propertyTax,

        // Roof estimates (key for roofing business!)
        roofSqft: result.roofEstimate?.roofSquareFootage,
        roofSqftMeasured: result.roofEstimate?.roofSquareFootageMeasured || false,
        roofPitch: result.roofEstimate?.avgPitchDegrees,
        roofSegments: result.roofEstimate?.segmentCount,
        roofAge: result.roofEstimate?.roofAge,
        buildingAge: result.roofEstimate?.buildingAge,
        roofAgeConfidence: result.roofEstimate?.roofAgeConfidence || 'low',
        roofAgeSource: result.roofEstimate?.roofAgeSource || 'year_built',
        materialCategory: result.roofEstimate?.materialCategory,
        materialLifespan: result.roofEstimate?.materialLifespan,
        needsRoofReplacement: result.roofEstimate?.needsReplacement,
        roofCostAsphalt: result.roofEstimate?.costEstimates?.asphalt?.estimate,
        roofCostMetal: result.roofEstimate?.costEstimates?.metal?.estimate,
        roofCostTile: result.roofEstimate?.costEstimates?.tile?.estimate,

        // Update old fields for compatibility
        estRoofSqft: result.roofEstimate?.roofSquareFootage || lead.estRoofSqft,
        estRoofCostLow: result.roofEstimate?.costEstimates?.asphalt?.estimate
          ? Math.round(result.roofEstimate.costEstimates.asphalt.estimate * 0.8)
          : lead.estRoofCostLow,
        estRoofCostHigh: result.roofEstimate?.costEstimates?.metal?.estimate || lead.estRoofCostHigh
      };
    });
  }, [lookupBatch]);

  // Get API stats
  const getApiStats = useCallback(async () => {
    try {
      const response = await fetch(`${PROPERTY_API_URL}/stats`);
      const data = await response.json();
      // Update local usage state
      if (data.usage) {
        setApiUsage(data.usage);
      }
      return data;
    } catch (e) {
      console.error('Failed to get stats:', e);
      return null;
    }
  }, []);

  // Get current API usage/credits
  const getUsage = useCallback(async () => {
    try {
      const response = await fetch(`${PROPERTY_API_URL}/usage`);
      const data = await response.json();
      setApiUsage(data);
      return data;
    } catch (e) {
      console.error('Failed to get usage:', e);
      return null;
    }
  }, []);

  // Clear local cache
  const clearLocalCache = useCallback(() => {
    localStorage.removeItem(CACHE_KEY);
  }, []);

  // Get local cache stats
  const getLocalCacheStats = useCallback(() => {
    const cached = getCachedLeads();
    const keys = Object.keys(cached);
    return {
      count: keys.length,
      sizeKb: Math.round(JSON.stringify(cached).length / 1024)
    };
  }, []);

  // Get user's daily limit status
  const getDailyLimit = useCallback(async () => {
    try {
      let url = `${PROPERTY_API_URL}/daily-limit?userId=${encodeURIComponent(userId)}`;
      if (emailRef.current) {
        url += `&email=${encodeURIComponent(emailRef.current)}`;
      }
      const response = await fetch(url);
      const data = await response.json();
      if (data.success) {
        setDailyLimit(data);
      }
      return data;
    } catch (e) {
      console.error('Failed to get daily limit:', e);
      return null;
    }
  }, [userId]);

  // Reward user for completing actions (earn more lookups)
  // Actions: flyer_generated, job_done, lead_deleted
  const rewardAction = useCallback(async (action, leadId = null) => {
    try {
      const response = await fetch(`${PROPERTY_API_URL}/reward`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId, action, leadId })
      });
      const data = await response.json();

      if (data.success) {
        // Refresh daily limit to reflect new bonus
        await getDailyLimit();
      }

      return data;
    } catch (e) {
      console.error('Failed to reward action:', e);
      return { success: false, error: e.message };
    }
  }, [userId, getDailyLimit]);

  return {
    lookupProperty,
    lookupBatch,
    enrichLeads,
    isLookingUp,
    lookupProgress,
    lookupError,
    apiUsage,
    dailyLimit,
    getApiStats,
    getUsage,
    getDailyLimit,
    rewardAction,
    clearLocalCache,
    getLocalCacheStats,
    userId
  };
}

export default usePropertyLookup;
